import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Tag, Database, Scale, Eye, HeartHandshake, PhoneCall } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { Calculator } from "@/components/site/Calculator";
import { CallbackModal } from "@/components/site/CallbackModal";
import { NewsletterBox } from "@/components/site/NewsletterBox";
import { Seo, ORG_SCHEMA, faqSchema } from "@/components/site/Seo";

const eur = (n) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(n || 0);

const HERO = "https://images.unsplash.com/photo-1544568100-847a948585b9?w=1400&q=80&auto=format&fit=crop";

const FAQ = [
  { q: "Quanto costa un'assicurazione per cane o gatto?", a: "Da 9,90€ a 35€ al mese. Il premio dipende da razza, età, città e massimale scelto." },
  { q: "Il preventivo è davvero gratuito?", a: "Sì. Il risultato è immediato e non richiede email. Solo se vuoi il report personalizzato ti chiediamo i contatti." },
  { q: "Polizzapet.it è una compagnia assicurativa?", a: "No. Siamo indipendenti: confrontiamo più compagnie e non lavoriamo per nessuna di loro. Alcuni link sono di affiliazione, segnalati in trasparenza." },
];
const STEPS = [["Ci racconti del tuo pet", "Cane o gatto, età, razza, città. Trenta secondi, niente dati personali."], ["Confrontiamo per te", "Leggiamo le condizioni che nessuno legge: franchigie, massimali, esclusioni. Te le spieghiamo in italiano."], ["Scegli con calma", "Nessuna pressione. Se non ti serve una polizza, te lo diciamo. Siamo animalisti prima che consulenti."]];
const TRUST = [
  [Scale, "Indipendenti", "Nessuna compagnia paga per la posizione in classifica. L'ordine lo decidono coperture e prezzo."],
  [Database, "Dati, non opinioni", "Stime basate su report ANMVI e listini raccolti da 60 cliniche italiane, aggiornati per razza, età e città."],
  [Eye, "Come guadagniamo", "Oggi tramite commissioni di affiliazione quando attivi una polizza dai nostri link; presto anche come intermediari iscritti IVASS, confrontando più compagnie. In entrambi i casi tu non paghi nulla in più."],
  [HeartHandshake, "Persone, non call center", "Abbiamo cani e gatti in casa. Se chiedi una richiamata, ti risponde una persona entro 48 ore."],
];

function LiveExample() {
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => { api.get("/public/calculator/examples").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!data?.examples?.length) return;
    const t = setInterval(() => setI((n) => (n + 1) % data.examples.length), 3500);
    return () => clearInterval(t);
  }, [data]);
  if (!data?.examples?.length) return null;
  const ex = data.examples[i];
  return (
    <div className="reveal reveal-3 mt-7 surface p-4 flex items-start gap-4 max-w-lg min-w-0" data-testid="hero-live-example">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rose-500/10 text-rose-500 font-mono text-xs font-bold">{ex.risk_level === "alto" ? "!!" : ex.risk_level === "medio" ? "!" : "ok"}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">Esempio reale dal calcolatore</p>
        <p className="text-sm font-medium leading-snug mt-0.5" key={i}><b>{ex.breed}</b>, {ex.age} anni, {ex.city} → emergenza <b className="text-rose-600 whitespace-nowrap">{eur(ex.emergency_min)}–{eur(ex.emergency_max)} €</b> · polizza da <b className="text-emerald-600 whitespace-nowrap">{ex.premium_min} €/mese</b></p>
      </div>
    </div>
  );
}

function ProofRow({ count }) {
  const items = [
    [ShieldCheck, "Indipendenti: nessuna compagnia paga per la posizione"],
    [Database, "Dati ANMVI + listini di 60 cliniche"],
    [Tag, "Codice sconto −25€ incluso"],
  ];
  if (count >= 100) items.unshift([CheckCircle2, `${count.toLocaleString("it-IT")} preventivi calcolati`]);
  return (
    <ul className="reveal reveal-4 mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="hero-proof">
      {items.map(([Icon, t]) => <li key={t} className="flex items-center gap-1.5"><Icon size={14} className="text-emerald-500 shrink-0" />{t}</li>)}
    </ul>
  );
}

function StickyCta() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("calcolatore");
    if (!hero) return;
    const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting && e.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(hero);
    return () => io.disconnect();
  }, []);
  if (!show) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-3 bg-background/90 backdrop-blur border-t border-border lg:hidden" data-testid="sticky-cta">
      <button onClick={() => document.getElementById("calcolatore")?.scrollIntoView({ behavior: "smooth" })} className="btn-primary w-full !py-3 text-sm">
        Calcola gratis in 30 secondi <ArrowRight size={16} />
      </button>
    </div>
  );
}

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [count, setCount] = useState(0);
  const [callback, setCallback] = useState(false);
  useEffect(() => {
    api.get("/public/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/calculator/examples").then((r) => setCount(r.data.calculations || 0)).catch(() => {});
  }, []);

  return (
    <>
      {callback && <CallbackModal onClose={() => setCallback(false)} />}
      <Seo title="Assicurazione cane e gatto: quanto costa davvero? Calcolatore gratuito | Polizzapet.it" description="Scopri in 30 secondi quanto rischi di spendere dal veterinario per razza, età e città. Confronto indipendente di 5 assicurazioni per cani e gatti, con codice sconto 25€." schema={[ORG_SCHEMA, faqSchema(FAQ)]} />

      <section className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.14),transparent_55%)]" />
        <div className="mx-auto max-w-7xl px-4 lg:px-8 pt-6 lg:pt-12 pb-16 grid gap-8 lg:gap-10 lg:grid-cols-[1fr_1.05fr] items-start">
          <div className="lg:pt-6 order-2 lg:order-1 min-w-0">
            <p className="reveal label-mono text-emerald-600 mb-4">Assicurazione cane e gatto · confronto indipendente</p>
            <h1 className="reveal reveal-1 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">Quanto ti costerebbe <span className="text-emerald-500">curare</span> il tuo cane o gatto? Scoprilo in 30 secondi.</h1>
            <p className="reveal reveal-2 mt-5 text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">Calcolatore gratuito per <b className="text-foreground">razza, età e città</b> + confronto onesto di 5 assicurazioni. Alla fine ti sblocchiamo un <b className="text-foreground">codice sconto da 25€</b>. E se una polizza non ti serve, te lo diciamo.</p>
            <div className="reveal reveal-3 mt-6 flex flex-wrap gap-3">
              <button onClick={() => document.getElementById("calcolatore")?.scrollIntoView({ behavior: "smooth" })} className="btn-primary text-sm lg:hidden" data-testid="hero-cta-calcola">Calcola gratis <ArrowRight size={16} /></button>
              <button onClick={() => setCallback(true)} className="btn-primary text-sm hidden lg:inline-flex" data-testid="hero-cta-callback-desktop"><PhoneCall size={15} /> Ti richiamiamo gratis in 10 minuti</button>
              <button onClick={() => setCallback(true)} className="btn-ghost text-sm lg:hidden" data-testid="hero-cta-callback"><PhoneCall size={15} /> Preferisci parlare? Ti richiamiamo</button>
            </div>
            <LiveExample />
            <ProofRow count={count} />
          </div>
          <div id="calcolatore" className="reveal reveal-1 order-1 lg:order-2 scroll-mt-24"><Calculator /></div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-16 grid gap-10 lg:grid-cols-[1fr_1.1fr] items-center" data-testid="why-us">
        <div className="relative">
          <img src={HERO} alt="Cane felice con la sua famiglia" className="rounded-3xl w-full h-[360px] object-cover" loading="lazy" />
          <div className="absolute -bottom-5 left-6 surface shadow-xl p-4 flex items-center gap-3" data-testid="hero-cost-badge">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-rose-500/10 text-rose-500"><Database size={20} /></span>
            <div><p className="text-xs text-muted-foreground">Torsione gastrica, cane taglia grande</p><p className="text-sm font-semibold"><span className="num">1.800 – 3.500 €</span> · fonte ANMVI</p></div>
          </div>
        </div>
        <div>
          <p className="label-mono text-emerald-600 mb-3">Perché scegliere noi</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Non lavoriamo per una compagnia. Lavoriamo per te.</h2>
          <div className="mt-8 space-y-5">
            {[["Dati veri, della tua città", "Listini di 60 cliniche e report ANMVI: la stessa ecografia costa 160€ a Milano e 65€ a Catania. Il nostro calcolatore lo sa."],
              ["Ti diciamo anche quando NON serve", "Meticcio giovane, città economica, fondo emergenze? Te lo scriviamo chiaro. Preferiamo perdere una commissione che farti pagare per niente."],
              ["Persone, non call center", "Abbiamo cani e gatti in casa. Se vuoi, ti richiamiamo noi: 10 minuti, zero gergo assicurativo, nessun obbligo."]].map(([t, d], i) => (
              <div key={t} className="flex gap-4"><span className="num text-emerald-500 pt-0.5">0{i + 1}</span><div><h3 className="font-display font-bold text-lg">{t}</h3><p className="text-sm text-muted-foreground leading-relaxed mt-1">{d}</p></div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-dark py-20" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <p className="label-mono text-emerald-600 mb-3">Il problema di cui nessuno parla</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">"Quanto costa?" non dovrebbe essere la prima domanda dal veterinario.</h2>
          <div className="grid gap-5 md:grid-cols-3 mt-12">
            {[["1.800 €", "il costo medio di una torsione dello stomaco in un cane di taglia grande (fonte ANMVI)"], ["160 € vs 65 €", "la stessa ecografia a Milano e a Catania: dove vivi cambia tutto"], ["da 9,90 €", "al mese: quanto costa non dover mai più scegliere tra portafoglio e cuore"]].map(([v, l]) => (
              <div key={v} className="surface p-6"><p className="num text-3xl text-emerald-600">{v}</p><p className="text-sm text-muted-foreground mt-3 leading-relaxed">{l}</p></div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/citta" className="btn-ghost text-sm" data-testid="problem-cta-citta">Quanto costa il veterinario nella tua città <ArrowRight size={14} /></Link>
            <Link to="/razze" className="btn-ghost text-sm" data-testid="problem-cta-razze">Rischi della tua razza <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-20 text-center" data-testid="how-it-works">
        <p className="label-mono text-emerald-500 mb-3">Come funziona</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Semplice come una carezza</h2>
        <div className="grid gap-5 md:grid-cols-3 mt-12 text-left">
          {STEPS.map(([t, d], i) => <div key={t} className="surface p-7"><p className="num text-emerald-500 text-sm mb-4">0{i + 1}</p><h3 className="font-display text-xl font-bold mb-2">{t}</h3><p className="text-sm text-muted-foreground leading-relaxed">{d}</p></div>)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-20" data-testid="trust">
        <p className="label-mono text-emerald-500 mb-3">Trasparenza</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">Siamo nuovi. Per questo ti diciamo tutto.</h2>
        <p className="text-muted-foreground max-w-2xl mb-10">Niente recensioni comprate, niente "migliaia di clienti" inventati. Ecco esattamente come lavoriamo e come guadagniamo.</p>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {TRUST.map(([Icon, t, d]) => <div key={t} className="surface p-6"><span className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600 mb-4"><Icon size={20} /></span><h3 className="font-display font-bold text-lg">{t}</h3><p className="text-sm text-muted-foreground leading-relaxed mt-2">{d}</p></div>)}
        </div>
        <Link to="/chi-siamo" className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline mt-8" data-testid="trust-chi-siamo">Chi c'è dietro Polizzapet.it <ArrowRight size={14} /></Link>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="home-articles">
        <div className="flex items-end justify-between mb-8">
          <div><p className="label-mono text-emerald-500 mb-3">Guide</p><h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Ultime guide</h2></div>
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline" data-testid="home-all-articles">Tutte le guide <ArrowRight size={14} /></Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {articles.map((a) => (
            <Link key={a.id} to={`/blog/${a.slug}`} className="group surface overflow-hidden hover:border-emerald-500/50 transition-colors" data-testid={`home-article-${a.slug}`}>
              {a.cover_image && <img src={imgUrl(a.cover_image)} alt={a.cover_alt || a.title} className="h-44 w-full object-cover" loading="lazy" />}
              <div className="p-5"><h3 className="font-display font-bold leading-snug group-hover:text-emerald-600 transition-colors">{a.title}</h3><p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.excerpt}</p></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-16 pb-24 lg:pb-16 grid gap-8 lg:grid-cols-[1.3fr_1fr] items-stretch">
        <div className="section-dark rounded-3xl p-10 sm:p-14 flex flex-col justify-center" data-testid="final-cta">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Il tuo pet non sa cos'è una franchigia. Noi sì.</h2>
          <p className="text-muted-foreground mt-4 max-w-lg">Raccontaci di lui o di lei. Ti richiamiamo noi entro 48 ore, senza impegno e senza costi.</p>
          <button onClick={() => setCallback(true)} className="btn-primary mt-8 self-start" data-testid="final-cta-consulenza"><PhoneCall size={16} /> Richiedi la richiamata gratuita</button>
          <Link to="/preventivo" className="text-sm text-emerald-600 underline mt-4 self-start" data-testid="final-cta-calcola">oppure calcola subito il preventivo <ArrowRight size={12} className="inline" /></Link>
        </div>
        <NewsletterBox />
      </section>
      <StickyCta />
    </>
  );
}
